import isEqual from 'fast-deep-equal';

import type { Primitive } from '#types/primitive.type';

import { isPrimitive } from '#misc/is-primitive.guard';

import type { ArrayComparer } from './types/array-comparer.type';

/**
 * Returns a new array containing only unique elements from the input array.
 *
 * - For primitive values, uniqueness is determined using a `Set`.
 * - For objects, uniqueness is determined using the provided comparer function (defaults to deep equality).
 *
 * @template T - The type of elements in the array, either a primitive or an object.
 * @param array - The array to filter for unique values.
 * @param compare - Optional comparer function to determine equality for non-primitive values.
 * Defaults to `fast-deep-equal`.
 * @returns A new array with only unique elements from the input array, preserving the original order.
 */
export function unique<T extends Primitive | Record<PropertyKey, unknown>>(
  array: T[],
  compare: ArrayComparer<T> = isEqual
): T[] {
  const primitives = new Set<Primitive>();
  let result: T[] = [];

  for (const item of array) {
    if (isPrimitive(item)) {
      if (primitives.has(item)) {
        continue;
      }

      primitives.add(item);
      result.push(item);
    } else {
      let found = false;

      for (const existing of result) {
        if (compare(item, existing)) {
          found = true;
          break;
        }
      }

      if (!found) {
        result = [...result, item];
      }
    }
  }

  return result;
}
